import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DropdownMenu, Button, Text, Flex, Switch } from '@radix-ui/themes';
import {
  ArrowRightIcon,
  BellIcon,
  DashboardIcon,
  GearIcon,
  MoonIcon,
} from '@/components/icons';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import { RoleBadge } from '../auth';

// Width and stroke used for every menu-row icon. Keeps the column of
// glyphs aligned on the left without per-row size tweaks.
const ROW_ICON_SIZE = 16;

export function UserAvatarMenu() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Same hook the profile Appearance card uses (`AccountSettingsPanel`)
  // — the dropdown toggle is a convenience surface that writes the
  // same preference store. Toggling explicitly picks light/dark (the
  // three-state Light/Dark/System control still lives in the profile
  // settings for users who want to follow the OS).
  const { isDark, setTheme } = useTheme();

  if (!user) return null;

  const isAdmin = hasRole?.('administrator') ?? false;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  const avatar = user.avatarUrl ? (
    <img
      src={user.avatarUrl}
      alt={user.displayName}
      className="h-[42px] w-[42px] border border-[var(--gray-6)]"
      style={{ borderRadius: '8px', objectFit: 'cover' }}
    />
  ) : (
    <div className="flex h-[42px] w-[42px] items-center justify-center text-base font-medium border border-[var(--gray-6)]" style={{ borderRadius: '8px', background: 'var(--gray-4)', color: 'var(--gray-11)' }}>
      {user.displayName.charAt(0).toUpperCase()}
    </div>
  );

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger>
        <Button variant="ghost" size="2" className="!h-[42px] !p-0" style={{ borderRadius: '8px', lineHeight: 0 }}>
          {avatar}
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="end" sideOffset={16} style={{ minWidth: '240px' }}>
        {/* User info header with profile link */}
        <div
          style={{ padding: '10px 12px', cursor: 'pointer' }}
          onClick={() => navigate('/profile?tab=settings')}
        >
          <Flex align="start" justify="between">
            <div>
              <Flex align="center" gap="2">
                <Text size="2" weight="bold" style={{ color: 'var(--gray-12)' }}>
                  {user.displayName}
                </Text>
                <RoleBadge role={user.role} size="sm" />
              </Flex>
              <Text size="1" style={{ display: 'block', marginTop: '2px', color: 'var(--gray-10)' }}>
                {user.email}
              </Text>
            </div>
            <ArrowRightIcon
              width={16}
              height={16}
              style={{ color: 'var(--gray-9)', marginTop: '2px', flexShrink: 0 }}
            />
          </Flex>
        </div>

        <DropdownMenu.Separator />

        <DropdownMenu.Item onClick={() => navigate('/profile?tab=dashboard')}>
          <Flex align="center" gap="3">
            <DashboardIcon width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
            <Text size="2">Dashboard</Text>
          </Flex>
        </DropdownMenu.Item>

        <DropdownMenu.Item onClick={() => navigate('/profile?tab=notifications')}>
          <Flex align="center" gap="3">
            <BellIcon width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
            <Text size="2">Notifications</Text>
          </Flex>
        </DropdownMenu.Item>

        {isAdmin && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={() => navigate('/profile?tab=admin')}>
              <Flex align="center" gap="3">
                <GearIcon width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
                <Text size="2">Admin</Text>
              </Flex>
            </DropdownMenu.Item>
          </>
        )}

        <DropdownMenu.Separator />

        {/* Dark-mode toggle. `onSelect={preventDefault}` keeps the
            dropdown open after each tap so the user can see the page
            flip without losing the menu (no need to reopen to undo).
            Click anywhere on the row flips the theme — the Switch is
            mirror state, not the click target, so taps on the label
            or icon work the same way. */}
        <DropdownMenu.Item
          onSelect={(e) => e.preventDefault()}
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
        >
          <Flex align="center" justify="between" width="100%">
            <Flex align="center" gap="3">
              <MoonIcon width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
              <Text size="2">Dark mode</Text>
            </Flex>
            <Switch
              size="1"
              // The Theme's inherited gray (sand) accent paints the
              // on-state track in a light grey that reads as "light
              // mode" in a dark menu. Override `--accent-track` to
              // the brand orange (#ff5310 — same swatch as BrandDot,
              // HeaderNav active marker, MegaMenuPanel hover, library
              // sidebar selection) so the on-state is the same
              // recognisable accent the rest of the app uses for
              // interactive selection. Off-state still uses the
              // default subtle gray track.
              style={
                {
                  '--accent-track': '#ff5310',
                } as React.CSSProperties
              }
              checked={isDark}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              // Stop the inner Switch click from bubbling — otherwise
              // the outer Item.onClick also fires and we'd toggle
              // twice (back to the original state).
              onClick={(e) => e.stopPropagation()}
            />
          </Flex>
        </DropdownMenu.Item>

        <DropdownMenu.Separator />

        <DropdownMenu.Item
          color="red"
          disabled={isLoggingOut}
          onClick={handleLogout}
        >
          {isLoggingOut ? 'Signing out...' : 'Sign Out'}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
